import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { ROUTES } from '../../constants/routes';
import { UI, DISPLAY, EASE, h2, SECTION_GAP } from '../../styles/tl5';
import { useGetPageSectionsQuery } from '../../store/siteContentApi';

// "Didn't find it? Build your own package."
//
// A bright teal panel: the pitch on the left, the four-step explainer on
// the right. The reference replaced the interactive concern-picker this
// component used to carry with a static explainer and a single CTA — the
// picking itself now happens on the Build Your Package page, so doing it
// twice would mean two sources of truth for the same flow.
// Fallback — mirrors DEFAULTS.home.build_your_own in pageSectionController.
const STEPS = [
  { n: '01', t: 'Pick your concerns', s: 'Heart, thyroid, diabetes, tiredness…' },
  { n: '02', t: 'See your price', s: 'Tests chosen and priced instantly' },
  { n: '03', t: 'Doctor approves', s: 'We notify you on WhatsApp' },
  { n: '04', t: 'Collected & tested', s: 'At home, report in ~6 hrs' },
];

const BuildYourOwn = () => {
  const { data: sections } = useGetPageSectionsQuery('home');
  const c = sections?.build_your_own || {};
  const steps = c.steps?.length ? c.steps : STEPS;
  const heading = c.heading || 'Didn\u2019t find it? Build your own package.';
  const lede = c.lede
    || 'Tell us what worries you, a body part, a condition or a symptom. We build the tests around it, a doctor checks it, and we collect at home.';

  return (
  <Box component="section" sx={{ width: '100%', maxWidth: 1320, mx: 'auto', pt: SECTION_GAP, px: 2 }}>
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 4.5,
        p: 'clamp(28px, 5vw, 56px)',
        borderRadius: '36px',
        bgcolor: 'var(--tl-teal)',
        color: '#FFFFFF',
      }}
    >
      <Box sx={{ flex: '1 1 380px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box
          component="span"
          sx={{
            alignSelf: 'start',
            px: '10px',
            py: '6px',
            borderRadius: '999px',
            bgcolor: 'var(--tl-yellow)',
            color: 'var(--tl-g9)',
            fontFamily: UI,
            fontSize: 12,
            fontWeight: 800,
          }}
        >
          New
        </Box>

        {/* The shared h2 sets a dark colour for light sections, so the
            panel's white has to be restated here. */}
        <Typography component="h2" sx={{ ...h2, color: '#FFFFFF' }}>
          {heading}
        </Typography>

        <Typography
          sx={{
            fontFamily: UI,
            fontSize: 17,
            lineHeight: 1.6,
            color: 'var(--tl-teal-50)',
            maxWidth: 460,
            m: 0,
          }}
        >
          {lede}
        </Typography>

        <Box
          component={Link}
          to={ROUTES.BUILD_PACKAGE}
          sx={{
            alignSelf: 'start',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            px: '22px',
            py: '15px',
            borderRadius: '16px',
            bgcolor: 'var(--tl-g9)',
            color: '#FFFFFF',
            fontFamily: UI,
            fontSize: 16,
            fontWeight: 800,
            textDecoration: 'none',
            transition: `transform .35s ${EASE}, background-color .25s`,
            '&:hover': { bgcolor: 'var(--tl-g8)', color: '#FFFFFF', transform: 'translateY(-2px)' },
          }}
        >
          Start building →
        </Box>
      </Box>

      <Box
        component="ol"
        sx={{
          flex: '1 1 420px',
          minWidth: 0,
          m: 0,
          p: 0,
          listStyle: 'none',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 180px), 1fr))',
          gap: 1,
        }}
      >
        {steps.map((s) => (
          <Box
            key={s.n}
            component="li"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '6px',
              p: '18px',
              borderRadius: '20px',
              bgcolor: 'rgba(11,43,39,0.22)',
            }}
          >
            <Box
              component="span"
              sx={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 28, color: 'var(--tl-yellow)' }}
            >
              {s.n}
            </Box>
            <Box component="span" sx={{ fontFamily: UI, fontSize: 16, fontWeight: 800 }}>
              {s.t}
            </Box>
            <Box component="span" sx={{ fontFamily: UI, fontSize: 13, color: 'var(--tl-teal-50)' }}>
              {s.s}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  </Box>
  );
};

export default BuildYourOwn;
